import React from 'react';
import {
  Shield, ClipboardCheck, Percent, DollarSign, FileText, ListChecks,
} from 'lucide-react';

// Read-only insurance proposal summary for the client portal. Mirrors the
// insurance ProposalForm in frontend/src/modules/crm/ProposalForm.jsx (separate
// React app) against the portal's own .proposal-summary-* CSS.
//
// `data` (proposal JSON) shape:
//   { services: [...], onboarding: {fee, discount, discount_type},
//     credentialing?: {provider_count, location_count, payer_count,
//                      rate_per_combo, discount, discount_type},
//     verification?: {days_per_week, patients_per_day, verification_type,
//                     weekly_fee, discount, discount_type},
//     billing?: {medical_production_pct, vision_production_pct, monthly_fee,
//                discount, discount_type},
//     notes }
// The credentialing/verification/billing blocks are only present when the
// matching service was selected.

function formatMoney(v) {
  if (v == null || v === '') return '$0.00';
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function pct(v) {
  if (v == null || v === '') return null;
  return `${Number(v)}%`;
}

function calcDiscount(base, discount, discountType) {
  if (!discount) return base;
  const d = Number(discount) || 0;
  if (discountType === 'percent') return base * (1 - d / 100);
  return Math.max(0, base - d);
}

function DiscountField({ block }) {
  if (!block || !Number(block.discount)) return null;
  return (
    <div className="proposal-summary-field">
      <span className="summary-label">Discount:</span>{' '}
      <span className="summary-value discount">
        {block.discount_type === 'percent' ? `${block.discount}%` : formatMoney(block.discount)}
      </span>
    </div>
  );
}

const SERVICE_LABELS = {
  credentialing: 'Credentialing',
  verification: 'Insurance Verification',
  billing: 'Billing',
};

export default function InsuranceProposalSummary({ data }) {
  if (!data) return <div className="empty-messages">No proposal data yet.</div>;

  const ob = data.onboarding   || {};
  const cr = data.credentialing || null;
  const vr = data.verification  || null;
  const bl = data.billing       || null;
  const services = Array.isArray(data.services) ? data.services : [];

  // Credentialing: mirror the backend formula exactly. Two models:
  // panel tiers (setup fee + Σ(price × panels) × providers × locations)
  // or the legacy per-combo (providers × locations × payers × rate).
  const crTiers = cr && Array.isArray(cr.panel_tiers) && cr.panel_tiers.length > 0
    ? cr.panel_tiers : null;
  const crCombos = cr
    ? (Number(cr.provider_count) || 0) * (Number(cr.location_count) || 0) * (Number(cr.payer_count) || 0)
    : 0;
  const crProviders = cr ? Math.max(1, Number(cr.provider_count) || 1) : 1;
  const crLocations = cr ? Math.max(1, Number(cr.location_count) || 1) : 1;
  const crPanelCount = crTiers
    ? crTiers.reduce((s, t) => s + (t.panels || []).filter(x => String(x).trim()).length, 0)
    : 0;
  const crGross = crTiers
    ? (Number(cr.setup_fee) || 0) + crTiers.reduce((s, t) =>
        s + (Number(t.price_each) || 0) * (t.panels || []).filter(x => String(x).trim()).length, 0)
      * crProviders * crLocations
    : crCombos * (Number(cr?.rate_per_combo) || 0);
  const crTotal = cr ? calcDiscount(crGross, cr.discount, cr.discount_type) : 0;

  const oneTimeTotal = calcDiscount(Number(ob.fee) || 0, ob.discount, ob.discount_type) + crTotal;
  // Fixed monthly portion only (production % and weekly verification fees are
  // usage-based and shown in their own sections, not a flat monthly total).
  const monthlyFixed = bl ? calcDiscount(Number(bl.monthly_fee) || 0, bl.discount, bl.discount_type) : 0;

  const hasProductionPct = bl && (Number(bl.medical_production_pct) > 0
    || Number(bl.vision_production_pct) > 0
    || Number(bl.emec_production_pct) > 0);

  return (
    <div className="proposal-summary">
      <div className="proposal-summary-totals">
        <div className="proposal-summary-total">
          <span className="proposal-summary-total-label">Monthly Recurring</span>
          <span className="proposal-summary-total-value">
            {monthlyFixed > 0 ? `${formatMoney(monthlyFixed)}/mo` : (hasProductionPct ? '% of production' : formatMoney(0) + '/mo')}
          </span>
        </div>
        <div className="proposal-summary-total">
          <span className="proposal-summary-total-label">{crTotal > 0 ? 'One-Time Total' : 'One-Time Onboarding'}</span>
          <span className="proposal-summary-total-value">{formatMoney(oneTimeTotal)}</span>
        </div>
      </div>

      {services.length > 0 && (
        <div className="proposal-summary-section">
          <div className="proposal-summary-section-title"><Shield size={12} /> Services Selected</div>
          <div className="proposal-summary-grid">
            <div className="proposal-summary-field">
              <span className="summary-value">
                {services.map(s => SERVICE_LABELS[s] || s).join(' · ')}
              </span>
            </div>
          </div>
        </div>
      )}

      {bl && (
        <div className="proposal-summary-section">
          <div className="proposal-summary-section-title"><Percent size={12} /> Billing</div>
          <div className="proposal-summary-grid">
            {pct(bl.medical_production_pct) && (
              <div className="proposal-summary-field">
                <span className="summary-label">Medical production:</span>{' '}
                <span className="summary-value">{pct(bl.medical_production_pct)} of collections</span>
              </div>
            )}
            {pct(bl.vision_production_pct) && (
              <div className="proposal-summary-field">
                <span className="summary-label">Vision production:</span>{' '}
                <span className="summary-value">{pct(bl.vision_production_pct)} of collections</span>
              </div>
            )}
            {pct(bl.emec_production_pct) && (
              <div className="proposal-summary-field">
                <span className="summary-label">EMEC production:</span>{' '}
                <span className="summary-value">{pct(bl.emec_production_pct)} of collections</span>
              </div>
            )}
            {Number(bl.monthly_fee) > 0 && (
              <div className="proposal-summary-field">
                <span className="summary-label">Monthly fee:</span>{' '}
                <span className="summary-value">{formatMoney(bl.monthly_fee)}/mo</span>
              </div>
            )}
            <DiscountField block={bl} />
          </div>
        </div>
      )}

      {vr && (
        <div className="proposal-summary-section">
          <div className="proposal-summary-section-title"><ClipboardCheck size={12} /> Insurance Verification</div>
          <div className="proposal-summary-grid">
            {vr.verification_type && (
              <div className="proposal-summary-field">
                <span className="summary-label">Type:</span>{' '}
                <span className="summary-value" style={{ textTransform: 'capitalize' }}>{vr.verification_type}</span>
              </div>
            )}
            {Number(vr.days_per_week) > 0 && (
              <div className="proposal-summary-field">
                <span className="summary-label">Schedule:</span>{' '}
                <span className="summary-value">
                  {vr.days_per_week} day{Number(vr.days_per_week) !== 1 ? 's' : ''}/week
                  {Number(vr.patients_per_day) > 0 ? `, ~${vr.patients_per_day} patients/day` : ''}
                </span>
              </div>
            )}
            {Number(vr.weekly_fee) > 0 && (
              <div className="proposal-summary-field">
                <span className="summary-label">Weekly fee:</span>{' '}
                <span className="summary-value">{formatMoney(vr.weekly_fee)}/wk</span>
              </div>
            )}
            <DiscountField block={vr} />
          </div>
        </div>
      )}

      {cr && crTiers && (
        <div className="proposal-summary-section">
          <div className="proposal-summary-section-title"><Shield size={12} /> Credentialing — Insurance Panels</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #2c5282' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: '#2c5282' }}>Insurance Panel / Service</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#2c5282' }}>Service Rate</th>
              </tr>
            </thead>
            <tbody>
              {Number(cr.setup_fee) > 0 && (
                <tr style={{ background: '#f6f8fa' }}>
                  <td style={{ padding: '6px 8px' }}>One Time Setup Fee</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatMoney(cr.setup_fee)}</td>
                </tr>
              )}
              {crTiers.map((tier, ti) => {
                const names = (tier.panels || []).filter(x => String(x).trim());
                if (names.length === 0) return null;
                return (
                  <React.Fragment key={tier.key || ti}>
                    <tr style={{ borderTop: '1px solid #cbd5e1' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 700 }}>{tier.label}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatMoney(tier.price_each)} each</td>
                    </tr>
                    {names.map((p, pi) => (
                      <tr key={pi} style={{ background: pi % 2 ? 'transparent' : '#f6f8fa' }}>
                        <td style={{ padding: '4px 8px 4px 16px' }}>{p}</td>
                        <td />
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
              <tr style={{ borderTop: '2px solid #2c5282', background: '#f6f8fa' }}>
                <td style={{ padding: '6px 8px', fontWeight: 700 }}>
                  Total{(crProviders > 1 || crLocations > 1)
                    ? ` (${crProviders} provider${crProviders !== 1 ? 's' : ''} × ${crLocations} location${crLocations !== 1 ? 's' : ''})` : ''}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>{formatMoney(crTotal)}</td>
              </tr>
            </tbody>
          </table>
          <div style={{ fontSize: 11, color: '#555', marginTop: 6 }}>
            • Unless otherwise specified, all quoted fees are calculated per provider, per practice location.
          </div>
          <DiscountField block={cr} />

          <div className="proposal-summary-section-title" style={{ marginTop: 14 }}>Estimated Timeline</div>
          <ul style={{ fontSize: 12, color: '#444', margin: '4px 0 0', paddingLeft: 18, lineHeight: 1.6 }}>
            <li>Most payer approvals occur within 30–120 days, depending on the insurance company's processing timelines</li>
            <li>Credentialing approval timelines are determined by the insurance carriers and cannot be guaranteed</li>
            <li>Quote includes one practice location; additional providers or locations may require additional credentialing fees</li>
            <li>The final insurance panel list may be adjusted if additional plans are identified during onboarding</li>
          </ul>

          <div className="proposal-summary-section-title" style={{ marginTop: 14 }}>Client Responsibilities &amp; Resubmission Policy</div>
          <ul style={{ fontSize: 12, color: '#444', margin: '4px 0 0', paddingLeft: 18, lineHeight: 1.6 }}>
            <li>To help ensure a smooth and timely credentialing process, clients are responsible for providing accurate, complete, and up-to-date information and documentation requested for credentialing and enrollment applications.</li>
            <li>If an application requires correction, resubmission, or significant revision as a result of inaccurate, incomplete, or outdated information provided by the client, additional service fees may apply. Any additional fees will be based on the extent of the revisions and work required.</li>
            <li>Macula Networks, LLC will communicate any applicable additional fees with the client in advance and obtain approval before completing any billable corrective or resubmission work.</li>
          </ul>
        </div>
      )}

      {cr && !crTiers && (
        <div className="proposal-summary-section">
          <div className="proposal-summary-section-title"><Shield size={12} /> Credentialing</div>
          <div className="proposal-summary-grid">
            {Number(cr.provider_count) > 0 && (
              <div className="proposal-summary-field">
                <span className="summary-label">Providers:</span>{' '}
                <span className="summary-value">{cr.provider_count}</span>
              </div>
            )}
            {Number(cr.location_count) > 0 && (
              <div className="proposal-summary-field">
                <span className="summary-label">Locations:</span>{' '}
                <span className="summary-value">{cr.location_count}</span>
              </div>
            )}
            {Number(cr.payer_count) > 0 && (
              <div className="proposal-summary-field">
                <span className="summary-label">Payers:</span>{' '}
                <span className="summary-value">{cr.payer_count}</span>
              </div>
            )}
            {Number(cr.rate_per_combo) > 0 && (
              <div className="proposal-summary-field">
                <span className="summary-label">Rate:</span>{' '}
                <span className="summary-value">{formatMoney(cr.rate_per_combo)} per provider/location/payer</span>
              </div>
            )}
            <DiscountField block={cr} />
            {crTotal > 0 && (
              <div className="proposal-summary-field">
                <span className="summary-label">Credentialing total:</span>{' '}
                <span className="summary-value"><strong>{formatMoney(crTotal)}</strong> ({crCombos} combination{crCombos === 1 ? '' : 's'} × {formatMoney(cr.rate_per_combo)})</span>
              </div>
            )}
          </div>
        </div>
      )}

      {Number(ob.fee) > 0 && (
        <div className="proposal-summary-section">
          <div className="proposal-summary-section-title"><DollarSign size={12} /> One-Time Onboarding</div>
          <div className="proposal-summary-grid">
            <div className="proposal-summary-field">
              <span className="summary-label">Fee:</span>{' '}
              <span className="summary-value">{formatMoney(ob.fee)}</span>
            </div>
            <DiscountField block={ob} />
          </div>
        </div>
      )}

      {Array.isArray(data.scope) && data.scope.length > 0 && (
        <div className="proposal-summary-section">
          <div className="proposal-summary-section-title"><ListChecks size={12} /> Scope of Work</div>
          {data.scope.map((grp, gi) => (
            <div key={gi} style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{grp.group}</div>
              {grp.note && (
                <div style={{ fontSize: 11, color: '#666', fontStyle: 'italic', marginBottom: 6 }}>{grp.note}</div>
              )}
              {(grp.items || []).map((it, ii) => (
                <div key={ii} style={{ marginBottom: 6 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{it.title}</div>
                  <div style={{ fontSize: 12, color: '#555', lineHeight: 1.4 }}>{it.description}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {data.notes && (
        <div className="proposal-summary-section">
          <div className="proposal-summary-section-title"><FileText size={12} /> Notes</div>
          <div className="proposal-summary-notes">{data.notes}</div>
        </div>
      )}
    </div>
  );
}
